import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatNumber, isoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Detail, Field, Input, Select, Textarea, useToast } from '../components/ui';
import type { LeaveBalance } from './Leaves';

/** Route: `/leaves/new` — ask for leave, seeing how many working days it takes first. */
export function LeaveRequestFormPage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = isoDate();
  const [form, setForm] = useState({ leaveTypeId: '', startDate: today, endDate: today, reason: '' });

  const types = useQuery({
    queryKey: ['leaves', 'types'],
    queryFn: () => api.get<any[]>('/leaves/types'),
  });

  const rangeOk = !!form.leaveTypeId && !!form.startDate && !!form.endDate && form.endDate >= form.startDate;
  const preview = useQuery({
    queryKey: ['leaves', 'preview', form.leaveTypeId, form.startDate, form.endDate],
    queryFn: () =>
      api.get<{ days: number; balance: LeaveBalance | null }>('/leaves/preview', {
        leaveTypeId: form.leaveTypeId,
        startDate: form.startDate,
        endDate: form.endDate,
      }),
    enabled: rangeOk,
  });

  const save = useMutation({
    mutationFn: () =>
      api.post('/leaves', {
        leaveTypeId: form.leaveTypeId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(t('leaves.requested'));
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      navigate('/leaves');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const balance = preview.data?.balance ?? null;
  const days = preview.data?.days ?? null;
  const over = balance?.remaining != null && days != null ? days - balance.remaining : 0;
  const selected = types.data?.find((ty) => ty.id === form.leaveTypeId);

  return (
    <FormPage
      title={t('leaves.new')}
      subtitle={t('leaves.slaNote')}
      backTo="/leaves"
      backLabel={t('leaves.title')}
      submitLabel={t('leaves.send')}
      submitting={save.isPending}
      disabled={!rangeOk || days === 0}
      loading={types.isLoading}
      error={types.error}
      onRetry={() => void types.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        rangeOk && (
          <dl>
            <Detail label={t('leaves.days')}>
              <span className="tabular">
                {preview.isLoading ? '…' : preview.error ? '—' : t('leaves.workingDays', { n: formatNumber(days, locale) })}
              </span>
            </Detail>
            {balance && (
              <Detail label={t('leaves.balance', { year: String(balance.year) })}>
                {balance.allowance == null ? (
                  t('leaves.noAllowance')
                ) : (
                  <span className="tabular">
                    {t('leaves.allowance')} {formatNumber(balance.allowance, locale)}
                    {!!balance.grantedDays && ` (${t('leaves.grantedDays', { n: formatNumber(balance.grantedDays, locale) })})`} · {t('leaves.used')}{' '}
                    {formatNumber(balance.approvedDays, locale)} · {t('leaves.pending')} {formatNumber(balance.pendingDays, locale)} ·{' '}
                    {t('leaves.remaining')} {formatNumber(balance.remaining, locale)}
                  </span>
                )}
              </Detail>
            )}
            {over > 0 && (
              <p className="pt-1 text-body-sm text-warning-fg dark:text-warning-dark">{t('leaves.overAllowance', { n: formatNumber(over, locale) })}</p>
            )}
            {preview.error && <p className="pt-1 text-body-sm text-error-fg dark:text-error-dark">{(preview.error as Error).message}</p>}
          </dl>
        )
      }
    >
      <FormGrid>
        <Field label={t('leaves.type')} required hint={selected ? (selected.isPaid ? t('leaves.paid') : t('leaves.unpaid')) : undefined}>
          <Select value={form.leaveTypeId} onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })} autoFocus>
            <option value="">—</option>
            {types.data?.map((ty) => (
              <option key={ty.id} value={ty.id}>{localized(ty)}</option>
            ))}
          </Select>
        </Field>
        <div className="hidden sm:block" aria-hidden />
        <Field label={t('leaves.from')} required>
          <Input
            type="date"
            value={form.startDate}
            onChange={(e) =>
              setForm({
                ...form,
                startDate: e.target.value,
                // Moving the start past the end drags the end along — a one-day leave stays one day.
                endDate: form.endDate < e.target.value ? e.target.value : form.endDate,
              })
            }
          />
        </Field>
        <Field label={t('leaves.to')} required hint={t('leaves.daysHint')}>
          <Input type="date" min={form.startDate} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </Field>
        <Field label={t('common.reason')} className="sm:col-span-2">
          <Textarea value={form.reason} maxLength={1000} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/leaves/:id/reject` — the requester reads the reason, so there must be one. */
export function RejectLeaveFormPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const request = useQuery({
    queryKey: ['leaves', 'one', id],
    queryFn: () => api.get<any>(`/leaves/${id}`),
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/leaves/${id}/reject`, { note: note.trim() }),
    onSuccess: () => {
      toast.success(t('leaves.rejected'));
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      // Back to the review list or the request's page, whichever it came from.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/leaves/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const r = request.data;

  return (
    <FormPage
      title={t('leaves.rejectTitle')}
      backLabel={t('common.back')}
      submitLabel={t('common.reject')}
      destructive
      submitting={reject.isPending}
      disabled={!note.trim() || (r && !r.can.decide)}
      loading={request.isLoading}
      error={request.error}
      onRetry={() => void request.refetch()}
      onSubmit={() => reject.mutate()}
      summary={
        r && (
          <dl>
            <Detail label={t('common.employee')}>{localized(r.user)}</Detail>
            <Detail label={t('leaves.type')}>{localized(r.leaveType)}</Detail>
            <Detail label={t('leaves.dates')}>
              <span className="tabular">
                {formatDate(r.startDate, locale)}
                {r.endDate !== r.startDate && <> – {formatDate(r.endDate, locale)}</>}
              </span>
            </Detail>
            <Detail label={t('leaves.days')}>{formatNumber(r.days, locale)}</Detail>
            {r.reason && <Detail label={t('common.reason')}>{r.reason}</Detail>}
          </dl>
        )
      }
    >
      <Field label={t('leaves.rejectReason')} required>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} autoFocus />
      </Field>
    </FormPage>
  );
}
